import React, { useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Stack,
} from "@mui/material";
import uniqueId from "lodash/uniqueId";
import { format } from "date-fns";

import { canShareMarker, resolveMarkerTargetIds } from "../../utils/markerMappers";
import GoalTargetsSelect from "./GoalTargetsSelect";
import { FIELD_LABEL_SX } from "./MarkerFields";
import type { MarkerFieldsProps, MarkerTargetsContext } from "./MarkerDialog.types";

type MarkerTargetsFieldsProps = Pick<MarkerFieldsProps, "values" | "onFieldChange"> & {
  /** Goals to offer and where the marker is added from. */
  context: MarkerTargetsContext;
  /** What the marker is called in the notice, e.g. "event" or "phase line". */
  markerNoun: string;
};

/**
 * "Apply marker to additional targets": the other targets of the current goal,
 * and/or targets picked from other goals. Only a date-linked external event can
 * be shared (see `canShareMarker`), so the section is disabled while the marker
 * is linked to a session — and phase lines don't render it at all.
 *
 * @param props
 * @param props.values        - Current form values
 * @param props.onFieldChange - Field setter owned by the dialog shell
 * @param props.context       - Goal options and the current goal/target
 * @param props.markerNoun    - Marker name used in the notice
 */
const MarkerTargetsFields: React.FC<MarkerTargetsFieldsProps> = ({
  values,
  onFieldChange,
  context,
  markerNoun,
}) => {
  // Created on the first render so the group is named from the start.
  const [labelId] = useState(() => uniqueId("marker-targets-label-"));

  const disabled = !canShareMarker(values);
  // The current goal is covered by its own checkbox.
  const otherGoals = context.goalOptions.filter((goal) => goal.id !== context.currentGoalId);
  // Resolved the same way the create body is, so the notice can't promise
  // targets that aren't sent — e.g. "Other clinical goals" ticked but empty.
  const appliesToMore = resolveMarkerTargetIds(values, context).length > 1;
  const eventDate = values.markerDate ? new Date(values.markerDate) : null;

  return (
    <Stack gap={4}>
      <FormControl component="fieldset">
        <FormLabel id={labelId} component="legend" sx={FIELD_LABEL_SX}>
          Apply marker to additional targets
        </FormLabel>

        <FormGroup aria-labelledby={labelId} sx={{ mt: 3, "& .Mui-disabled+.MuiFormControlLabel-label": { color: "text.disabled" } }}>
          <FormControlLabel
            key="apply-to-current-goal"
            disabled={disabled}
            control={
              <Checkbox
                size="small"
                checked={values.applyToCurrentGoal}
                onChange={(event) => onFieldChange("applyToCurrentGoal", event.target.checked)}
              />
            }
            label="All child targets within current clinical goal"
            componentsProps={{ typography: { variant: "body.small" } }}
          />

          <FormControlLabel
            key="apply-to-other-goals"
            disabled={disabled}
            control={
              <Checkbox
                size="small"
                checked={values.applyToOtherGoals}
                onChange={(event) => onFieldChange("applyToOtherGoals", event.target.checked)}
              />
            }
            label="Other clinical goals"
            componentsProps={{ typography: { variant: "body.small" } }}
          />
        </FormGroup>
      </FormControl>

      {values.applyToOtherGoals && (
        <Box sx={{ pl: 5 }}>
          <GoalTargetsSelect
            goals={otherGoals}
            value={values.otherTargetIds}
            onChange={(targetIds) => onFieldChange("otherTargetIds", targetIds)}
            disabled={disabled}
          />
        </Box>
      )}

      {/* Copy pending design: Figma's notice describes status/creation-date rules
          that don't apply — any non-archived target can have markers. */}
      {appliesToMore && !disabled && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          This {markerNoun} will appear on any target that was active on {eventDate ? format(eventDate, "PPP") : "the selected date"}.
          Targets that were already archived or created after that date won&apos;t show it.
        </Alert>
      )}
    </Stack>
  );
};

export default MarkerTargetsFields;
